<template>
    <ai-form-text-box-dictionary
        ref="textboxRef"
        v-model:value="value"
        :fieldNameText="meta.fieldNameText"
        :fieldNameId="meta.fieldNameId"
        :validationRules="meta.validationRules"
        :display-expr="meta.extParams.displayExpr"
        :value-expr="meta.extParams.valueExpr"
        :title="meta.title"
        :data-source="dict"
        @popup-shown="setData"
        @popup-hidden='onPopupClose'
    >
        <div class='flex' style='margin-bottom: 10px'>
            <div :class='[{"disabled-mode": viewType === "table"}]'>
                <dx-button text='Таблица' hint='Изменить представление на таблицу' icon='/infoenergo/assets/tep/tep-cards/table.svg'
                           :disabled='viewType === "table"' :on-click='() => toggleMode("table")' />
            </div>
            <div :class='[{"disabled-mode": viewType === "tree"}]'>
                <dx-button text='Дерево' hint='Изменить представление на дерево' icon='/infoenergo/assets/tep/tep-cards/tree.svg'
                           :disabled='viewType === "tree"' :on-click='() => toggleMode("tree")' />
            </div>
            <ai-text-box @input="onFilter" :value='searchValue' placeholder='Поиск...' style='margin-left: 10px'/>
        </div>
        <div class='flex h-full'>
            <div class='flex flex-col tep-tarif-col-1'>
                <template v-if='viewType === "table"'>
                    <ai-grid-ro
                        ref='tarifGridRef'
                        :meta="tarifGridMeta"
                        :args="tarifSearchArgs"
                        :to-remove-toolbar-items-names="removeGridToolbarItems"
                        :show-grid-footer='false'
                        @row-click='onTarifClickGrid'
                        @toolbar-button-click='onChoiceClick'
                    >
                        <template #cellTemplate="{ data }">
                            <div class="relative flex align-center w-full">
                                <div class="min-h-[16px] w-full">
                                    {{ data.displayValue }}
                                </div>
                            </div>
                        </template>
                    </ai-grid-ro>
                </template>
                <template v-else>
                    <ai-tree-list-edit
                        :meta="tarifTreeMeta"
                        :args="tarifSearchArgs"
                        ref='tarifTreeRef'
                        :to-remove-toolbar-items-names="removeTreeToolbarItems"
                        @cell-click='onTarifClickTree'
                        @toolbar-button-click='onChoiceClick'
                        :show-grid-footer='false'
                    />
                </template>
            </div>
            <div class='flex flex-col' style='min-width: 10px; width: 10px'></div>
            <div class='flex flex-col tep-tarif-col-2'>
                <div class='h-full' v-if='rateGridLoaded'>
                    <ai-grid-ro
                        ref='rateGridRef'
                        :meta='tarifRateMeta'
                        :args='tarifRateArgs'
                        :to-remove-toolbar-items-names="removeRateToolbarItems"
                        :show-grid-footer='false'
                    />
                </div>
            </div>
        </div>
    </ai-form-text-box-dictionary>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import DxButton from 'devextreme-vue/button';
import tepCardsStore from '@/views/tep/tep-grid/store/tep-cards-store';
import AiFormTextBoxDictionary from '@/components/card-component/ai-form-text-box-dictionary.vue';
import AiGridRo from '@/components/ai-grid-ro/ai-grid-ro.vue';
import AiTextBox from '@/components/ai-text-box.vue';
import AiTreeListEdit from '@/components/ai-tree-list-edit/ai-tree-list-edit.vue';

const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    callbacks: {
        type: Object,
        default: () => ({ userDataChanged: null }),
    },
});

const userData = inject('userData');
const validationResult = inject('validationResult');
const viewType = ref('table');
const textboxRef = ref();
const rateGridLoaded = ref(false);
const tarifGridRef = ref();
const tarifTreeRef = ref();
const rateGridRef = ref();

const removeGridToolbarItems = ['refresh', 'export', 'settings', 'show-column-chooser', 'searchPanel'];
const removeTreeToolbarItems = ['refresh', 'export', 'settings', 'show-column-chooser', 'searchPanel', 'copy', 'edit',
    'addSibling', 'addChild', 'delete', 'save', 'expandOrCollapseButton', 'moveSave', 'moveTo', 'find-prev', 'find', 'find-start', 'find-next'];
const removeRateToolbarItems = ['choice', 'refresh', 'export', 'settings', 'show-column-chooser', 'searchPanel'];

const tarifSearchMeta = {
    title: 'Тарифы',
    keyExpr: 'tarif',
    api: {select: 'tep-grid./tep/all-tarifs-data/get'},
    rights: {IDU: false},
    wordWrapEnabled: true,
    columns: [
        {
            caption: 'Код тарифа',
            name: 'tarif',
            dataField: 'tarif',
            visible: false,
            width: 50,
            index: 1,
            cellTemplate: 'cellTemplate'
        },
        {
            caption: '№ тарифа',
            name: 'npp',
            dataField: 'npp',
            width: '10%',
            index: 2,
            cellTemplate: 'cellTemplate'
        },
        {
            caption: 'Название',
            name: 'sname',
            dataField: 'sname',
            width: '30%',
            index: 3,
            cellTemplate: 'cellTemplate'
        },
        {
            caption: 'Название',
            name: 'name',
            dataField: 'name',
            width: '30%',
            index: 4,
            cellTemplate: 'cellTemplate'
        },
        {
            caption: 'Интервал',
            name: 'interval_name',
            dataField: 'interval_name',
            width: '10%',
            index: 5,
            cellTemplate: 'cellTemplate'
        },
        {
            caption: 'Тепло/электр',
            name: 'tep_el_name',
            dataField: 'tep_el_name',
            width: '10%',
            index: 6,
            cellTemplate: 'cellTemplate'
        },
        {
            caption: 'Статус',
            name: 'pr_active_name',
            dataField: 'pr_active_name',
            width: '10%',
            index: 7,
            cellTemplate: 'cellTemplate'
        }
    ]
};
const tarifGridMeta = {
    ...tarifSearchMeta,
    viewId: 'tep-tarif-search-grid'
}
const tarifTreeMeta = {
    ...tarifSearchMeta,
    viewId: 'tep-tarif-search-tree',
    parentIdExpr: 'tarif_parent',
    selectButtonMode: 'single',
    selectionMode: 'single'
}
const tarifSearchDefaultArgs = {searchStr: ""};
const tarifSearchArgs = ref(tarifSearchDefaultArgs);

const tarifRateDefaultColumns = [
    {
        caption: 'Дата начала',
        name: 'dat_beg',
        dataField: 'dat_beg',
        dataType: 'date',
        width: '25%',
        index: 1
    },
    {
        caption: 'Дата окончания',
        name: 'dat_end',
        dataField: 'dat_end',
        dataType: 'date',
        width: '25%',
        index: 2
    },
    {
        caption: 'Ставка 1',
        name: 'rate1',
        dataField: 'rate1',
        dataType: 'number',
        width: '16.67%',
        index: 3
    },
    {
        caption: 'Ставка 2',
        name: 'rate2',
        dataField: 'rate2',
        dataType: 'number',
        width: '16.67%',
        index: 4
    },
    {
        caption: 'Ставка 3',
        name: 'rate3',
        dataField: 'rate3',
        dataType: 'number',
        width: '16.67%',
        index: 5
    },
    {
        caption: 'Ставка 4',
        name: 'rate4',
        dataField: 'rate4',
        dataType: 'number',
        width: 80,
        index: -1,
        visible: false
    }
];
const tarifRateMeta = ref({
    title: 'Действующая ставка тарифа',
    keyExpr: 'tarif',
    selectionMode: 'single',
    api: {select: 'tep-grid./tep/tarif-rate-data/get'},
    rights: {IDU: false},
    wordWrapEnabled: true,
    columns: tarifRateDefaultColumns,
    viewId: 'tep-tarif-search-rate'
});
const tarifRateDefaultArgs = {tarif: 0};
const tarifRateArgs = ref(tarifRateDefaultArgs);

const tarifData = ref({});
const searchValue = ref('');
const searchTimer = ref(null);

const value = computed(() => {
    if (userData.value[props.meta.fieldNameText] === undefined) {
        return null;
    }
    return userData.value[props.meta.fieldNameText];
});

const dict = computed(() =>
    [
        {
            [`${props.meta.extParams.valueExpr}`]: userData.value[props.meta.fieldNameId],
            [`${props.meta.extParams.displayExpr}`]: userData.value[props.meta.fieldNameText],
        },
    ]
);

const setRateColumnsHeader = (tepEl, tipTarifSost) => {
    const currRateMeta = {...tarifRateMeta.value};
    const ratesList = tepCardsStore.state.allRatesList;
    if (tipTarifSost && ratesList.length > 0) {
        const rateData = ratesList.filter(item => item.tip_tarif_sost === tipTarifSost);
        if (rateData.length > 0) {
            for (const item of currRateMeta.columns) {
                if (item.name === 'rate1') {
                    if (rateData[0].name1) item.caption = rateData[0].name1;
                } else if (item.name === 'rate2') {
                    if (rateData[0].name2) item.caption = rateData[0].name2;
                } else if (item.name === 'rate3') {
                    item.caption = "...";
                }
            }
        }
    } else {
        if (tepEl === 2) {
            for (const item of currRateMeta.columns) {
                if (item.name === 'rate1') {
                    item.caption = "Ставка за ГКал";
                } else if (item.name === 'rate2') {
                    item.caption = "Ставка за тонны";
                } else if (item.name === 'rate3') {
                    item.caption = "Ставка за куб.м";
                }
            }
        } else {
            for (const item of currRateMeta.columns) {
                if (item.name === 'rate1') {
                    item.caption = "Ставка по энергии";
                } else if (item.name === 'rate2') {
                    item.caption = "Ставка за мощность";
                } else if (item.name === 'rate3') {
                    item.caption = "...";
                }
            }
        }
    }
    tarifRateMeta.value = currRateMeta;
}

const changeRateData = async (tepEl, tipTarif, tarif) => {
    rateGridLoaded.value = false;
    setRateColumnsHeader(tepEl, tipTarif);
    const rateSearchArgs = {...tarifRateDefaultArgs};
    rateSearchArgs.tarif = tarif;
    rateGridLoaded.value = true;
    await rateGridRef.value?.setDataSource(null, rateSearchArgs);
}

const setData = async (loadRates = true) => {
    await tarifGridRef.value?.setDataSource(null, tarifSearchArgs.value);
    await tarifTreeRef.value?.setDataSource(null, tarifSearchArgs.value);
    await tarifTreeRef.value?.expandAll();
    if (loadRates) await changeRateData(2, null, 0); // значения для очистки грида
}

const toggleMode = async (type) => {
    viewType.value = type;
    setTimeout(async () => {
        await setData();
    }, 200);
}

const onFilter = ({newText}) => {
    searchValue.value = newText;
    clearTimeout(searchTimer.value);
    searchTimer.value = setTimeout(async () => {
        if (searchValue.value.length > 2) {
            const searchArgs = {...tarifSearchDefaultArgs};
            searchArgs.searchStr = searchValue.value;
            tarifSearchArgs.value = searchArgs;
            await setData(false);
        }
    }, 1800);
}

const onTarifClickGrid = async (event) => {
    const tarifDataL = event.data;
    tarifData.value = {...tarifDataL};
    await changeRateData(tarifDataL.tep_el, tarifDataL.tip_tarif_sost, tarifDataL.tarif);
}

const onTarifClickTree = async (event) => {
    const treeData = await tarifTreeRef.value?.getTreeListDataSource();
    let tarifDataL = treeData.filter(el => el.tarif === event.key);
    tarifDataL = tarifDataL.length > 0 ? tarifDataL[0] : {};
    tarifData.value = {...tarifDataL};
    await changeRateData(tarifDataL.tep_el ?? 2, tarifDataL.tip_tarif_sost ?? null, tarifDataL.tarif ?? 0);
}

const onChoiceClick = () => {
    textboxRef.value?.popupInstance.hide();
    validationResult.value = validationResult.value.filter(s => s.fieldNameText !== props.meta.fieldNameText);
    // в tarifData.value уже лежит то, что выбрали
    userData.value[props.meta.fieldNameText] = tarifData.value[props.meta.extParams.displayExpr];
    userData.value[props.meta.fieldNameId] = tarifData.value[props.meta.extParams.valueExpr];
    if (props.callbacks.userDataChanged) {
        const ratesData = rateGridRef.value?.getGridDataSource();
        props.callbacks.userDataChanged({tarifData: tarifData.value, ratesData});
    }
    onPopupClose();
}

const onPopupClose = () => {
    tarifData.value = {};
    searchValue.value = '';
    tarifSearchArgs.value = {...tarifSearchDefaultArgs};
    tarifRateArgs.value = {...tarifRateDefaultArgs};
    toggleMode('table') // для изначального отображения и очистки данных в гридах и дереве
}
</script>

<style scoped>
.disabled-mode {
    opacity: 0.5;
}

.tep-tarif-col-1 {
    flex: 0 0 60%;
    min-width: 800px;
}

.tep-tarif-col-2 {
    flex: 0 0 40%;
    min-width: 600px;
}

</style>
